import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { listChannelConnections } from '../channels/channelsApi'
import type { ChannelConnection } from '../channels/types'
import { Thumb } from '../components/ui/Avatar'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { ChannelDot } from '../components/ui/Channel'
import { channelDisplayName } from '../components/ui/channelDisplay'
import { Icon, I } from '../components/ui/icons'
import { Pill } from '../components/ui/Pill'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { exportProductsStock, getProductOptions, listProducts } from '../products/productsApi'
import { OptionLinkHint } from '../products/OptionLinkHint'
import { optionListing, optionSyncState } from '../products/productOptions'
import type { ProductChannelListingSummary, ProductListItem, ProductOptions, ProductPage, ProductStatus } from '../products/types'
import { getSourceAuthority } from '../source/sourceApi'
import { ProductsEmptyPage } from './ProductsEmptyPage'

const PAGE_SIZE = 10
const STATUS_OPTIONS: Array<ProductStatus | ''> = ['', 'ACTIVE', 'DRAFT', 'INACTIVE']
const DEFAULT_STOCK_EXPORT_FILENAME = 'products-stock.xlsx'

type StatusTone = 'success' | 'warning' | 'neutral'

function statusTone(status: ProductStatus): StatusTone {
  if (status === 'ACTIVE') return 'success'
  if (status === 'DRAFT') return 'warning'
  return 'neutral'
}

function formatMoney(value: number | null | undefined, locale: string): string {
  const amount = Number(value ?? 0)
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount)
}

function formatDate(value: string, locale: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.products.errors.codes.${error.code}`
  }
  return 'pages.products.errors.codes.REQUEST_FAILED'
}

function exportErrorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.products.export.errors.codes.${error.code}`
  }
  return 'pages.products.export.errors.codes.REQUEST_FAILED'
}

function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = window.URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    window.URL.revokeObjectURL(objectUrl)
  }
}

function listingQuantityText(listings: ProductChannelListingSummary[], locale: string, t: ReturnType<typeof useTranslation>['t']): string | null {
  const finiteQuantity = listings.reduce<number | null>((sum, listing) => {
    if (listing.finiteStockQuantity == null) return sum
    return (sum ?? 0) + listing.finiteStockQuantity
  }, null)
  const parts: string[] = []
  if (finiteQuantity != null) {
    parts.push(t('pages.products.channelSync.quantity', { quantity: finiteQuantity.toLocaleString(locale) }))
  }
  if (listings.some(listing => listing.hasInfiniteStock)) {
    parts.push(t('pages.products.channelSync.infinite'))
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

function ChannelSyncCell({ listings, locale }: { listings?: ProductChannelListingSummary[]; locale: string }) {
  const { t } = useTranslation()
  const rows = listings ?? []

  if (rows.length === 0) {
    return <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>—</span>
  }

  const providers = Array.from(new Set(rows.map(listing => channelDisplayName(listing.provider))))
  const primaryProvider = providers[0] ?? channelDisplayName(rows[0].provider)
  const listingCountKey = rows.length === 1 ? 'pages.products.channelSync.listingCount' : 'pages.products.channelSync.listingCount_plural'
  const countText = providers.length === 1
    ? t(listingCountKey, { channel: primaryProvider, count: rows.length })
    : t('pages.products.channelSync.multiCount', { count: rows.length })
  const quantityText = listingQuantityText(rows, locale, t)

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', maxWidth: 260 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {providers.map(provider => <ChannelDot key={provider} name={provider} size={20} />)}
      </span>
      <span style={{ color: 'var(--fg)', fontWeight: 800 }}>{countText}</span>
      {quantityText && <span style={{ color: 'var(--fg-muted)', fontWeight: 700 }}>{quantityText}</span>}
    </div>
  )
}

interface ExpandedOptions {
  loading: boolean
  error: unknown
  data: ProductOptions | null
}

function StockCell({ product, locale }: { product: ProductListItem; locale: string }) {
  const { t } = useTranslation()
  const optionCount = product.optionCount ?? 0
  return (
    <div style={{ display: 'grid' }}>
      <span style={{ color: 'var(--fg)', fontWeight: 700 }}>{(product.availableQty ?? 0).toLocaleString(locale)}</span>
      <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)', lineHeight: '15px' }}>
        {optionCount > 0 ? t('pages.products.options.totalAcross', { count: optionCount }) : t('pages.products.options.available')}
      </span>
    </div>
  )
}

const SYNC_PILL_TONE = { success: 'success', warning: 'warning', danger: 'danger', neutral: 'neutral' } as const

function OptionRows({ product, expanded, tlaaqIsSource, colSpan, locale, onRetry }: {
  product: ProductListItem
  expanded: ExpandedOptions
  tlaaqIsSource: boolean
  colSpan: number
  locale: string
  onRetry: () => void
}) {
  const { t } = useTranslation()
  const rowStyle = { background: 'var(--ink-50)', borderTop: '1px solid var(--border-subtle)' }
  if (expanded.loading || expanded.error !== null) {
    return (
      <tr style={rowStyle}>
        <td colSpan={colSpan} style={{ padding: '10px 16px', paddingInlineStart: 72, fontSize: 12.5, fontWeight: 700, color: expanded.error !== null ? 'var(--danger)' : 'var(--fg-muted)' }}>
          {expanded.error !== null ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {t('pages.products.options.loadError')}
              <Button kind="secondary" size="xs" icon={I.refresh} onClick={onRetry}>{t('pages.products.options.retry')}</Button>
            </span>
          ) : t('pages.products.options.loading')}
        </td>
      </tr>
    )
  }
  return (
    <>
      {(expanded.data?.options ?? []).map(option => {
        const sync = optionSyncState(option, tlaaqIsSource, t, locale)
        return (
          <tr key={option.id} style={rowStyle} aria-label={t('pages.products.options.rowLabel', { parent: product.name, option: option.optionLabel ?? option.name })}>
            <td />
            <td style={{ padding: '9px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 20, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                  <div style={{ width: 14, height: 12, borderBottom: '1.5px solid var(--border-default)', borderInlineStart: '1.5px solid var(--border-default)' }} />
                </div>
                <div style={{ display: 'grid', gap: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 700, color: 'var(--fg)', fontSize: 13 }}>{option.optionLabel ?? option.name}</span>
                  <OptionLinkHint />
                </div>
              </div>
            </td>
            <td style={{ padding: '9px 16px', color: 'var(--fg-subtle)', fontWeight: 700 }}>—</td>
            <td />
            <td style={{ padding: '9px 16px', color: 'var(--fg-muted)', fontWeight: 600 }}>
              {t('pages.products.price', { value: formatMoney(option.price, locale) })}
            </td>
            <td style={{ padding: '9px 16px', color: option.availableQty === 0 ? 'var(--danger-700)' : 'var(--fg)', fontWeight: 700 }}>
              {option.availableQty.toLocaleString(locale)}
            </td>
            <td style={{ padding: '9px 16px' }}>
              {sync ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                  <ChannelDot name={channelDisplayName(optionListing(option)!.provider)} size={18} />
                  <Pill tone={SYNC_PILL_TONE[sync.tone]} dot size="sm">{sync.label}</Pill>
                </span>
              ) : <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>—</span>}
            </td>
            <td style={{ padding: '9px 16px', textAlign: 'end', color: 'var(--fg-subtle)', fontSize: 12 }}>{sync?.detail ?? ''}</td>
          </tr>
        )
      })}
    </>
  )
}

function MobileOptions({ product, expanded, tlaaqIsSource, locale, onToggle, onRetry }: {
  product: ProductListItem
  expanded: ExpandedOptions | undefined
  tlaaqIsSource: boolean
  locale: string
  onToggle: () => void
  onRetry: () => void
}) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <Button kind="secondary" size="xs" onClick={onToggle} style={{ justifySelf: 'start' }}>
        {t(expanded ? 'pages.products.options.hide' : 'pages.products.options.show', { name: product.name })}
      </Button>
      {expanded?.loading && <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', fontWeight: 700 }}>{t('pages.products.options.loading')}</span>}
      {expanded && expanded.error !== null && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--danger)', fontWeight: 700 }}>
          {t('pages.products.options.loadError')}
          <Button kind="secondary" size="xs" icon={I.refresh} onClick={onRetry}>{t('pages.products.options.retry')}</Button>
        </span>
      )}
      {(expanded?.data?.options ?? []).map(option => {
        const sync = optionSyncState(option, tlaaqIsSource, t, locale)
        return (
          <div key={option.id} style={{ display: 'grid', gap: 6, padding: 12, borderRadius: 10, background: 'var(--ink-50)', minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: 'var(--fg)', fontSize: 13 }}>{option.optionLabel ?? option.name}</span>
              {sync && <Pill tone={SYNC_PILL_TONE[sync.tone]} dot size="sm">{sync.label}</Pill>}
            </div>
            <OptionLinkHint />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5 }}>
              <span style={{ color: 'var(--fg-muted)', fontWeight: 600 }}>{t('pages.products.price', { value: formatMoney(option.price, locale) })}</span>
              <strong style={{ color: 'var(--fg)' }}>{t('pages.products.options.availableCount', { quantity: option.availableQty.toLocaleString(locale) })}</strong>
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface ProductsPageProps {
  accessToken: string
  onOpenDetail?: (productId: number) => void
  onCreateProduct?: () => void
}

export function ProductsPage({ accessToken, onOpenDetail, onCreateProduct }: ProductsPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [status, setStatus] = useState<ProductStatus | ''>('')
  const [listedChannelConnectionId, setListedChannelConnectionId] = useState<number | undefined>()
  const [channels, setChannels] = useState<ChannelConnection[] | null>(null)
  const [channelsError, setChannelsError] = useState<unknown>(null)
  const [page, setPage] = useState(0)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [data, setData] = useState<ProductPage<ProductListItem> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<unknown>(null)
  const [expanded, setExpanded] = useState<Record<number, ExpandedOptions>>({})
  // Loaded with the first expanded parent: sync labels differ when Tlaaq owns stock.
  const [tlaaqIsStockSource, setTlaaqIsStockSource] = useState<boolean | null>(null)

  const productSubtitle = (product: ProductListItem) => {
    const images = t('pages.products.imageCount', { count: product.imageCount })
    return (product.optionCount ?? 0) > 0 ? `${images} · ${t('pages.products.options.withoutSku')}` : images
  }

  const loadOptions = (productId: number) => {
    setExpanded(prev => ({ ...prev, [productId]: { loading: true, error: null, data: null } }))
    if (tlaaqIsStockSource === null) {
      getSourceAuthority(accessToken, 'STOCK')
        .then(authority => setTlaaqIsStockSource(authority.ownerType === 'TLAAQ'))
        .catch(() => setTlaaqIsStockSource(false))
    }
    getProductOptions(accessToken, productId)
      .then(data => setExpanded(prev => prev[productId] ? { ...prev, [productId]: { loading: false, error: null, data } } : prev))
      .catch(caught => setExpanded(prev => prev[productId] ? { ...prev, [productId]: { loading: false, error: caught, data: null } } : prev))
  }

  const toggleOptions = (productId: number) => {
    if (expanded[productId]) {
      setExpanded(prev => {
        const next = { ...prev }
        delete next[productId]
        return next
      })
      return
    }
    loadOptions(productId)
  }

  useEffect(() => {
    let cancelled = false
    listChannelConnections(accessToken)
      .then(response => {
        if (!cancelled) {
          setChannels(response)
          setChannelsError(null)
        }
      })
      .catch(caught => {
        if (!cancelled) setChannelsError(caught)
      })
    return () => { cancelled = true }
  }, [accessToken])

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQuery(query)
      setPage(0)
    }, 300)
    return () => window.clearTimeout(id)
  }, [query])

  useEffect(() => {
    let cancelled = false
    // Intentional reset on dep change so the table shows a loading state while
    // re-fetching after query/page/status changes. The rule's general guidance
    // (avoid cascading renders) doesn't fit this canonical fetch-on-deps pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)

    listProducts(accessToken, { page, size: PAGE_SIZE, query: debouncedQuery, status, listedChannelConnectionId })
      .then(response => {
        if (cancelled) return
        setData(response)
      })
      .catch(caught => {
        if (cancelled) return
        setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, debouncedQuery, listedChannelConnectionId, page, status])

  const rows = useMemo<ProductListItem[]>(() => data?.items ?? [], [data?.items])
  const total = data?.totalElements ?? 0
  const activeCount = useMemo(() => rows.filter(product => product.status === 'ACTIVE').length, [rows])
  const draftCount = useMemo(() => rows.filter(product => product.status === 'DRAFT').length, [rows])

  const toggleRow = (id: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleExport = async () => {
    setExportError(null)
    setExporting(true)
    try {
      const download = await exportProductsStock(accessToken, { query: debouncedQuery, status, listedChannelConnectionId })
      downloadBlob(download.blob, download.filename ?? DEFAULT_STOCK_EXPORT_FILENAME)
    } catch (caught) {
      setExportError(caught)
    } finally {
      setExporting(false)
    }
  }

  const isUnfilteredEmptyCatalog = !loading && error === null && channelsError === null && data && data.totalElements === 0 && !debouncedQuery.trim() && !status && listedChannelConnectionId == null && page === 0

  if (isUnfilteredEmptyCatalog) {
    return <ProductsEmptyPage onManualCreate={onCreateProduct} />
  }

  const headers = [
    t('pages.products.table.product'),
    t('pages.products.table.sku'),
    t('pages.products.table.status'),
    t('pages.products.table.price'),
    t('pages.products.table.stock'),
    t('pages.products.table.channelSync'),
    t('pages.products.table.updated'),
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <div>
          <p style={{ fontSize: 14, color: 'var(--fg-muted)', margin: 0 }}>
            {t('pages.products.description')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: isMobile ? '100%' : undefined }}>
          <Button kind="secondary" size="sm" icon={I.download} disabled={loading || exporting} onClick={handleExport}>
            {t(exporting ? 'pages.products.export.exporting' : 'pages.products.actions.export')}
          </Button>
          <Button kind="primary" size="sm" icon={I.plus} onClick={onCreateProduct}>{t('pages.products.actions.newProduct')}</Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: t('pages.products.stats.total'), value: loading ? '…' : total.toLocaleString(i18n.language) },
          { label: t('pages.products.stats.activeOnPage'), value: loading ? '…' : activeCount.toLocaleString(i18n.language) },
          { label: t('pages.products.stats.draftOnPage'), value: loading ? '…' : draftCount.toLocaleString(i18n.language) },
        ].map(stat => (
          <Card key={stat.label} padding={18}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{stat.label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em', marginTop: 6, lineHeight: 1 }}>{stat.value}</div>
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ position: 'relative', flex: isMobile ? '1 1 100%' : 1, minWidth: isMobile ? 0 : 240 }}>
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>{t('pages.products.searchLabel')}</span>
          <div style={{ position: 'absolute', insetInlineStart: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-subtle)', display: 'flex' }}>
            <Icon size={15}>{I.search}</Icon>
          </div>
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t('pages.products.searchPlaceholder')}
            style={{
              width: '100%', paddingBlock: '9px', paddingInlineStart: 32, paddingInlineEnd: 14,
              fontSize: 13, fontFamily: 'inherit', color: 'var(--fg)',
              border: '1px solid var(--border-default)', borderRadius: 9, outline: 'none',
              background: 'white',
            }}
          />
        </label>
        <label style={{ display: isMobile ? 'grid' : 'inline-flex', gridTemplateColumns: isMobile ? '1fr' : undefined, alignItems: isMobile ? 'stretch' : 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)', width: isMobile ? '100%' : undefined }}>
          {t('pages.products.filters.status')}
          <select
            value={status}
            onChange={event => {
              setStatus(event.target.value as ProductStatus | '')
              setPage(0)
            }}
            style={{
              padding: '9px 12px', fontSize: 13, fontWeight: 600, color: 'var(--fg)',
              background: 'white', border: '1px solid var(--border-subtle)', borderRadius: 9,
              cursor: 'pointer', fontFamily: 'inherit', minWidth: isMobile ? 0 : 150, width: isMobile ? '100%' : undefined,
            }}
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option || 'all'} value={option}>{t(option ? `pages.products.status.${option}` : 'pages.products.filters.allStatuses')}</option>
            ))}
          </select>
        </label>
        <label style={{ display: isMobile ? 'grid' : 'inline-flex', gridTemplateColumns: isMobile ? '1fr' : undefined, alignItems: isMobile ? 'stretch' : 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)', width: isMobile ? '100%' : undefined }}>
          {t('pages.products.filters.channel')}
          <select
            value={listedChannelConnectionId ?? ''}
            disabled={channels === null}
            onChange={event => {
              setListedChannelConnectionId(event.target.value ? Number(event.target.value) : undefined)
              setPage(0)
            }}
            style={{
              padding: '9px 12px', fontSize: 13, fontWeight: 600, color: 'var(--fg)',
              background: 'white', border: '1px solid var(--border-subtle)', borderRadius: 9,
              cursor: 'pointer', fontFamily: 'inherit', minWidth: isMobile ? 0 : 170, width: isMobile ? '100%' : undefined,
            }}
          >
            <option value="">{t('pages.products.filters.allChannels')}</option>
            {(channels ?? []).map(channel => (
              <option key={channel.id} value={channel.id}>{channel.displayName} · {t(`channels.providers.${channel.provider}`)}</option>
            ))}
          </select>
        </label>
      </div>

      {exportError !== null && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(exportErrorMessageKey(exportError), { defaultValue: String(t('pages.products.export.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      {loading && (
        <Card padding={22} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.refresh}</Icon>
            {t('pages.products.loading')}
          </div>
        </Card>
      )}

      {(error !== null || channelsError !== null) && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(error ?? channelsError), { defaultValue: String(t('pages.products.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      <Card padding={0}>
        {isMobile ? (
          <div style={{ display: 'grid' }}>
            {!loading && rows.length === 0 && (
              <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                {t('pages.products.emptyFiltered')}
              </div>
            )}
            {rows.map(product => {
              const selected = selectedRows.has(product.id)
              const detailContent = (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', gap: 12, alignItems: 'center' }}>
                    {product.primaryImage?.publicUrl
                      ? <img src={product.primaryImage.publicUrl} alt={product.primaryImage.altText ?? product.name} style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      : <Thumb name={product.name} size={44} hue={product.id % 8} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: 'var(--fg)', fontSize: 14, lineHeight: 1.25 }}>{product.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 2 }}>{productSubtitle(product)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: 8, fontSize: 12.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.products.table.sku')}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg)', overflowWrap: 'anywhere' }}>{(product.optionCount ?? 0) > 0 ? '—' : product.sku}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.products.table.status')}</span>
                      <Pill tone={statusTone(product.status)} dot size="sm">{t(`pages.products.status.${product.status}`)}</Pill>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.products.table.price')}</span>
                      <strong style={{ color: 'var(--fg)' }}>{t('pages.products.price', { value: formatMoney(product.price, i18n.language) })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.products.table.stock')}</span>
                      <span style={{ textAlign: 'end' }}><StockCell product={product} locale={i18n.language} /></span>
                    </div>
                    <div title={(product.channelListings ?? []).length > 0 ? t('pages.products.channelSyncTooltip') : t('pages.products.channelSync.empty')}>
                      <ChannelSyncCell listings={product.channelListings} locale={i18n.language} />
                    </div>
                  </div>
                </>
              )

              return (
                <div
                  key={product.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: 12,
                    padding: 16,
                    borderTop: '1px solid var(--border-subtle)',
                    background: selected ? 'var(--brand-100)' : undefined,
                    alignItems: 'start',
                  }}
                >
                  <input aria-label={t('pages.products.table.selectRow', { name: product.name })} type="checkbox" checked={selected} onChange={() => toggleRow(product.id)} style={{ cursor: 'pointer', marginTop: 14 }} />
                  <div style={{ display: 'grid', gap: 12 }}>
                    {detailContent}
                    {(product.optionCount ?? 0) > 0 && (
                      <MobileOptions
                        product={product}
                        expanded={expanded[product.id]}
                        tlaaqIsSource={tlaaqIsStockSource === true}
                        locale={i18n.language}
                        onToggle={() => toggleOptions(product.id)}
                        onRetry={() => loadOptions(product.id)}
                      />
                    )}
                    {onOpenDetail && (
                      <button
                        type="button"
                        aria-label={String(t('pages.products.actions.openDetail', { name: product.name }))}
                        onClick={() => onOpenDetail(product.id)}
                        style={{
                          justifySelf: 'end',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '7px 10px',
                          borderRadius: 8,
                          border: '1px solid var(--border-subtle)',
                          background: 'white',
                          color: 'var(--fg-muted)',
                          font: 'inherit',
                          fontSize: 12,
                          fontWeight: 800,
                          textAlign: 'start',
                          cursor: 'pointer',
                        }}
                      >
                        <Icon size={15}>{I.eye}</Icon>
                        {t('pages.products.actions.openDetail', { name: product.name })}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <th style={{ width: 40, padding: '11px 16px' }}>
                  <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>{t('pages.products.table.select')}</span>
                </th>
                {headers.map((header, index) => (
                  <th key={header} style={{ padding: '11px 16px', textAlign: index === headers.length - 1 ? 'end' : 'start', fontWeight: 700, fontSize: 11.5, color: 'var(--fg-subtle)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={headers.length + 1} style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                    {t('pages.products.emptyFiltered')}
                  </td>
                </tr>
              )}
              {rows.map(product => {
                const selected = selectedRows.has(product.id)
                const optionCount = product.optionCount ?? 0
                const optionsOpen = expanded[product.id]
                return (
                  <Fragment key={product.id}>
                  <tr
                    onClick={() => onOpenDetail?.(product.id)}
                    style={{
                      borderTop: '1px solid var(--border-subtle)',
                      background: selected ? 'var(--brand-100)' : undefined,
                      transition: 'background 100ms',
                      cursor: onOpenDetail ? 'pointer' : undefined,
                    }}
                  >
                    <td style={{ padding: '12px 16px' }} onClick={event => event.stopPropagation()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {optionCount > 0 ? (
                          <button
                            type="button"
                            aria-expanded={Boolean(optionsOpen)}
                            aria-label={String(t(optionsOpen ? 'pages.products.options.hide' : 'pages.products.options.show', { name: product.name }))}
                            onClick={() => toggleOptions(product.id)}
                            style={{ display: 'inline-flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brand-700)' }}
                          >
                            <Icon size={16} stroke={2.25} style={{ transform: optionsOpen ? undefined : `rotate(${i18n.dir() === 'rtl' ? 90 : -90}deg)` }}>{I.chevronD}</Icon>
                          </button>
                        ) : <span style={{ width: 16, flexShrink: 0 }} />}
                        <input aria-label={t('pages.products.table.selectRow', { name: product.name })} type="checkbox" checked={selected} onChange={() => toggleRow(product.id)} style={{ cursor: 'pointer' }} />
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {product.primaryImage?.publicUrl
                          ? <img src={product.primaryImage.publicUrl} alt={product.primaryImage.altText ?? product.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                          : <Thumb name={product.name} size={40} hue={product.id % 8} />}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 600, color: 'var(--fg)', fontSize: 13 }}>{product.name}</span>
                            {optionCount > 0 && <Pill tone="brand" size="sm" style={{ padding: '1px 8px' }}>{t('pages.products.options.count', { count: optionCount })}</Pill>}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 1 }}>{productSubtitle(product)}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: optionCount > 0 ? undefined : 'var(--font-mono)', fontSize: optionCount > 0 ? 13 : 11.5, fontWeight: optionCount > 0 ? 700 : undefined, color: optionCount > 0 ? 'var(--fg-subtle)' : 'var(--fg-muted)' }}>
                      {optionCount > 0 ? '—' : product.sku}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <Pill tone={statusTone(product.status)} dot size="sm">{t(`pages.products.status.${product.status}`)}</Pill>
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--fg)' }}>
                      {t('pages.products.price', { value: formatMoney(product.price, i18n.language) })}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <StockCell product={product} locale={i18n.language} />
                    </td>
                    <td style={{ padding: '12px 16px' }} title={(product.channelListings ?? []).length > 0 ? t('pages.products.channelSyncTooltip') : t('pages.products.channelSync.empty')}>
                      <ChannelSyncCell listings={product.channelListings} locale={i18n.language} />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'end', color: 'var(--fg-muted)', fontSize: 12 }}>
                      {formatDate(product.updatedAt, i18n.language)}
                    </td>
                  </tr>
                  {optionsOpen && (
                    <OptionRows
                      product={product}
                      expanded={optionsOpen}
                      tlaaqIsSource={tlaaqIsStockSource === true}
                      colSpan={headers.length + 1}
                      locale={i18n.language}
                      onRetry={() => loadOptions(product.id)}
                    />
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
          <span style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
            {t('pages.products.pagination', {
              from: total === 0 ? 0 : page * PAGE_SIZE + 1,
              to: Math.min((page + 1) * PAGE_SIZE, total),
              total,
            })}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button kind="secondary" size="xs" disabled={page === 0 || loading} onClick={() => setPage(current => Math.max(0, current - 1))}>{t('pages.products.paginationPrev')}</Button>
            <Button kind="secondary" size="xs" disabled={!data || page >= data.totalPages - 1 || loading} onClick={() => setPage(current => current + 1)}>{t('pages.products.paginationNext')}</Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
